import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "@/i18n/useTranslation";
import { History, ExternalLink } from "lucide-react";
import { useScriptVersions } from "../hooks";
import type { Script } from "@/features/dashboard/types";

interface VersionColumnProps {
  script: Script;
  selectedVersionId?: string;
  onVersionChange: (versionId: string) => void;
  onOpenVersionControl: () => void;
  formatDate: (date: string) => string;
}

export const VersionColumn = ({
  script,
  selectedVersionId,
  onVersionChange,
  onOpenVersionControl,
  formatDate,
}: VersionColumnProps) => {
  const { t } = useTranslation();
  const { data: versions, isLoading } = useScriptVersions(script.id);

  // Use selected version or current version or first version
  const activeVersionId =
    selectedVersionId || script.current_version_id || versions?.[0]?.id;

  const activeVersion = versions?.find((v) => v.id === activeVersionId);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 py-2">
        <div className="h-5 w-20 bg-muted animate-pulse rounded" />
        <div className="h-8 w-full bg-muted animate-pulse rounded" />
      </div>
    );
  }

  if (!versions || versions.length === 0) {
    return (
      <div className="flex flex-col gap-2 py-2">
        <span className="text-sm text-muted-foreground">
          {t("versions.no_versions")}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 py-0.5">
      {/* Version Name/Number Display */}
      <div className="font-semibold text-xs text-slate-800 tracking-tight truncate">
        {activeVersion?.version_name
          ? `v${activeVersion.version_number}: ${activeVersion.version_name}`
          : `Version ${activeVersion?.version_number || 1}`}
      </div>

      {/* Version Selector */}
      <Select value={activeVersionId} onValueChange={onVersionChange}>
        <SelectTrigger className="h-8 text-xs bg-white border-slate-200 shadow-2xs">
          <SelectValue
            placeholder={
              activeVersion
                ? `v${activeVersion.version_number}${activeVersion.id === script.current_version_id ? ` (${t("versions.latest")})` : ""}`
                : undefined
            }
          />
        </SelectTrigger>
        <SelectContent>
          {versions.map((version) => (
            <SelectItem key={version.id} value={version.id}>
              <div className="flex items-center justify-between gap-2 text-xs">
                <span>v{version.version_number}</span>
                {version.id === script.current_version_id && (
                  <span className="text-[11px] text-muted-foreground">
                    ({t("versions.latest")})
                  </span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Action Buttons */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onOpenVersionControl}
          className="h-7 px-2 gap-1 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-200/60"
        >
          <History className="h-3 w-3" />
          {t("versions.history_button")}
        </Button>

        {activeVersion?.script_url && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-slate-600 hover:text-slate-900 hover:bg-slate-200/60"
            title={t("versions.view_script")}
            onClick={() => window.open(activeVersion.script_url, "_blank", "noopener,noreferrer")}
          >
            <ExternalLink className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
};
